import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { from, lastValueFrom, Observable } from "rxjs";
import { map, timeout, retry } from "rxjs/operators";
import { History } from "../models/history";
import { Contracts } from "../models/contracts";
import { Register, CustomerInfo, ModifyUserRequest, ContractInviteRequest, ContractInviteResponse } from "../models/user";
import { Downloadproducts } from "../models/downloads";
import { Activations, ActivationsAdapter, ActivateLicense } from "../models/activations";
import { Password } from "../models/security";
import { OfflineSurrenderRequest, tokenRequest } from "../models/tokens";
import { environment } from "../../environments/environment";

@Injectable({
  providedIn: "root",
})
export class PortalService {
  responseTimeOut = 120000;
  newApiUrl = environment.APIURL;

  constructor(
    private http: HttpClient,
    private adapter: ActivationsAdapter,
  ) {}

  showTokens(c: Contracts) {
    return ["CLONEMANAGER", "PROTECT_FOR_VMS", "VA", "FSBLOCKER"].includes(c.product);
  }

  disableSurrenderTab(c: Contracts) {
    return ["CLONEMANAGER", "VA", "FSBLOCKER"].includes(c.product);
  }

  disableTokenTab(c: Contracts) {
    return !["CLONEMANAGER", "FSBLOCKER", "VA"].includes(c.product);
  }

  disableActivateTab(c: Contracts) {
    return this.showTokens(c);
  }

  getDisplayName(p: string): string {
    switch (p) {
      case "CLONEMANAGER":
        return "CloneManager";
      case "COBMR":
        return "CoBMR";
      case "SYSBACK":
        return "SysBack";
      case "PROTECT_FOR_VMS":
        return "Protect for VMs";
      case "FSBLOCKER":
        return "FSBlocker";
      default:
        return p;
    }
  }

  deleteUser(username: string, password: string): Promise<void> {
    return lastValueFrom(
      this.http.post<void>(`${this.newApiUrl}/users/delete`, {
        username,
        password,
      }),
    );
  }

  getMaintenance(): Promise<{
    start: Date;
    duration: number;
    message: string;
    pending: boolean;
  }> {
    return lastValueFrom(
      this.http.get<any>(`${this.newApiUrl}/maintenance`).pipe(
        // if res == null return null
        map((res) =>
          res
            ? {
                start: new Date(res.start),
                duration: res.durationHours,
                message: res.message,
                pending: res.pending,
              }
            : null,
        ),
      ),
    );
  }

  getHistory(): Promise<History[]> {
    return lastValueFrom(
      this.http.get<History[]>(`${this.newApiUrl}/history`).pipe(
        map((response) =>
          response.map((h) => ({
            ...h,
            product: this.getDisplayName(h.product),
          })),
        ),
        timeout(this.responseTimeOut),
        retry(1),
      ),
    );
  }

  getContractHistory(id: number): Promise<History[]> {
    return lastValueFrom(
      this.http.get<History[]>(`${this.newApiUrl}/contracts/${id}/history`).pipe(
        map((response) =>
          response.map((h) => ({
            ...h,
            product: this.getDisplayName(h.product),
          })),
        ),
        timeout(this.responseTimeOut),
        retry(1),
      ),
    );
  }

  GetContractHistory(id: number): Observable<History[]> {
    return from(this.getContractHistory(id));
  }

  async getContracts(): Promise<Contracts[]> {
    const r = await lastValueFrom(this.http.get<Contracts[]>(`${this.newApiUrl}/contracts`));
    return r.map((c) => ({
      ...c,
      displayName: this.getDisplayName(c.product),
    }));
  }

  GetContracts(): Observable<Contracts[]> {
    return from(this.getContracts());
  }

  async getDownloads(product: string): Promise<Downloadproducts> {
    const prods: Downloadproducts = await lastValueFrom(
      this.http.get<Downloadproducts>(`${this.newApiUrl}/downloads/list?product=${product}`),
    );

    const vaProduct = prods?.va;
    const latest = Array.isArray(vaProduct?.latest) ? vaProduct.latest[0] : vaProduct?.latest;
    const archive = Array.isArray(vaProduct?.archive) ? vaProduct.archive[0] : vaProduct?.archive;

    if (latest?.isos?.length) {
      latest.installs = [...(latest.installs || []), ...latest.isos];
    }

    if (archive?.isos?.length) {
      archive.installs = [...(archive.installs || []), ...archive.isos];
    }

    return prods;
  }

  trackDownload(username: string, product: string, item: string): Promise<void> {
    return lastValueFrom(
      this.http
        .post<void>(`${this.newApiUrl}/downloads/track?username=${username}&product=${product}&item=${item}`, null)
        .pipe(timeout(this.responseTimeOut), retry(1)),
    );
  }

  TrackDownload(username: string, product: string, item: string): Promise<void> {
    return this.trackDownload(username, product, item);
  }

  getActivations(contractID: number, showTokens = false): Promise<Activations[]> {
    return lastValueFrom(
      this.http.get<any[]>(`${this.newApiUrl}/contracts/${contractID}/activations?showtokens=${showTokens}`).pipe(
        map((data) => data.map((item) => this.adapter.adapt(item))),
        timeout(this.responseTimeOut),
        retry(1),
      ),
    );
  }

  GetActivations(contractID: number, showTokens = false): Observable<Activations[]> {
    return from(this.getActivations(contractID, showTokens));
  }

  exportActivations(contractID: number): Promise<string> {
    return lastValueFrom(this.http.get(`${this.newApiUrl}/contracts/${contractID}/activations/export`, { responseType: "text" }));
  }

  offlineSurrender(r: OfflineSurrenderRequest, contractID: number): Promise<void> {
    return lastValueFrom(this.http.post<void>(`${this.newApiUrl}/contracts/${contractID}/return/offline`, r));
  }

  queryContract(contractID: number): Promise<Contracts> {
    return lastValueFrom(
      this.http.get<Contracts[]>(`${this.newApiUrl}/contracts`).pipe(map((res) => res.find((c) => c.id === contractID))),
    );
  }

  getCustomerInfo(id: number): Promise<CustomerInfo> {
    return lastValueFrom(
      this.http.get<CustomerInfo>(`${this.newApiUrl}/contracts/${id}/address`).pipe(timeout(this.responseTimeOut), retry(1)),
    );
  }

  GetCustomerInfo(id: number): Observable<CustomerInfo> {
    return from(this.getCustomerInfo(id));
  }

  surrenderLicense(contractId: number, data: any): Promise<any> {
    return lastValueFrom(
      this.http.post(`${this.newApiUrl}/contracts/${contractId}/return`, data).pipe(timeout(this.responseTimeOut), retry(1)),
    );
  }

  SurrenderLicense(contractId: number, data: any): Observable<any> {
    return from(this.surrenderLicense(contractId, data));
  }

  activateLicense(data: ActivateLicense[], contractId: number): Promise<any> {
    return lastValueFrom(
      this.http
        .post(`${this.newApiUrl}/contracts/${contractId}/activations`, JSON.stringify(data))
        .pipe(timeout(this.responseTimeOut), retry(1)),
    );
  }

  ActivateLicense(data: ActivateLicense[], contractId: number): Observable<any> {
    return from(this.activateLicense(data, contractId));
  }

  requestToken(data: tokenRequest[], contractId: number): Promise<any> {
    return lastValueFrom(
      this.http
        .post(`${this.newApiUrl}/contracts/${contractId}/tokens`, JSON.stringify(data))
        .pipe(timeout(this.responseTimeOut), retry(1)),
    );
  }

  RequestToken(data: tokenRequest[], contractId: number): Observable<any> {
    return from(this.requestToken(data, contractId));
  }

  addContract(data: any): Promise<any> {
    return lastValueFrom(this.http.post(`${this.newApiUrl}/contracts`, JSON.stringify(data)).pipe(timeout(this.responseTimeOut), retry(1)));
  }

  AddContract(data: any): Observable<any> {
    return from(this.addContract(data));
  }

  getUser(id: number): Promise<any> {
    return lastValueFrom(this.http.get(`${this.newApiUrl}/users/${id}`).pipe(timeout(this.responseTimeOut), retry(1)));
  }

  updateUser(id: number, data: ModifyUserRequest): Promise<any> {
    return lastValueFrom(this.http.put(`${this.newApiUrl}/users/${id}/update`, data).pipe(timeout(this.responseTimeOut), retry(1)));
  }

  registerUser(data: Register): Promise<any> {
    return lastValueFrom(this.http.post(`${this.newApiUrl}/users/register`, data).pipe(timeout(this.responseTimeOut), retry(1)));
  }

  getContractInvite(token: string): Promise<ContractInviteResponse> {
    return lastValueFrom(
      this.http.get<ContractInviteResponse>(`${this.newApiUrl}/users/invites/${encodeURIComponent(token)}`).pipe(timeout(this.responseTimeOut), retry(1)),
    );
  }

  acceptContractInvite(token: string): Promise<void> {
    return lastValueFrom(
      this.http.post<void>(`${this.newApiUrl}/users/invites/${encodeURIComponent(token)}/accept`, null).pipe(timeout(this.responseTimeOut), retry(1)),
    );
  }

  createContractInvite(contractNo: number, data: ContractInviteRequest): Promise<ContractInviteResponse> {
    return lastValueFrom(
      this.http.post<ContractInviteResponse>(`${this.newApiUrl}/contracts/${contractNo}/invites`, data).pipe(timeout(this.responseTimeOut), retry(1)),
    );
  }

  getContractInvites(contractNo: number): Promise<ContractInviteResponse[]> {
    return lastValueFrom(
      this.http.get<ContractInviteResponse[]>(`${this.newApiUrl}/contracts/${contractNo}/invites`).pipe(timeout(this.responseTimeOut), retry(1)),
    );
  }

  resendContractInvite(contractNo: number, token: string): Promise<ContractInviteResponse> {
    return lastValueFrom(
      this.http.post<ContractInviteResponse>(`${this.newApiUrl}/contracts/${contractNo}/invites/${encodeURIComponent(token)}/resend`, null).pipe(timeout(this.responseTimeOut), retry(1)),
    );
  }

  deleteContractInvite(contractNo: number, token: string): Promise<void> {
    return lastValueFrom(
      this.http.delete<void>(`${this.newApiUrl}/contracts/${contractNo}/invites/${encodeURIComponent(token)}`).pipe(timeout(this.responseTimeOut), retry(1)),
    );
  }

  changePassword(id: number, data: Password): Promise<any> {
    return lastValueFrom(this.http.put(`${this.newApiUrl}/users/${id}/password`, data).pipe(timeout(this.responseTimeOut), retry(1)));
  }

  getResetQuestion(username: string): Promise<any> {
    return lastValueFrom(
      this.http.get(`${this.newApiUrl}/users/resetquestion?username=${username}`).pipe(timeout(this.responseTimeOut), retry(1)),
    );
  }

  resetPassword(username: string, answer: string): Promise<any> {
    return lastValueFrom(
      this.http
        .post(`${this.newApiUrl}/users/resetquestion?username=${username}&answer=${answer}`, null)
        .pipe(timeout(this.responseTimeOut), retry(1)),
    );
  }

  getCountries(): Promise<any> {
    return lastValueFrom(this.http.get("https://restcountries.com/v2/all?fields=nativeName").pipe(timeout(this.responseTimeOut), retry(1)));
  }
}
